import React, { useState, useEffect, useCallback, useRef } from 'react';
import PaymentHoldOverlay from '../components/business/PaymentHoldOverlay';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { resolveAssetUrl, itemImages } from '../lib/assets';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ImageLightbox } from '../components/business/ImageLightbox';
import { UtensilsCrossed, Flame, Loader2, Check, Sparkles, CalendarClock, Users, CreditCard, ShoppingBag, Plus, Minus, Store, Truck, X } from 'lucide-react';
import BusinessExtras from '../components/BusinessExtras';
import BusinessGiftCards from '../components/business/BusinessGiftCards';
import DeliveryStatusBanner from '../components/business/DeliveryStatusBanner';
import ContactBusinessButton from '../components/ContactBusinessButton';
import { HoursMapSection, PromotionsSection } from '../components/business/PublicSections';
import DeliveryAddressField from '../components/business/DeliveryAddressField';
import { pluginsFor, orderedSections, pluginEnabled } from '../data/businessCatalog';
import { deliveryStatus, formatDeliveryHours, deliveryCountdown, nextOpenLabel } from '../lib/deliveryHours';
import { pageVars } from '../data/motifs';
import { useAuth } from '../contexts/AuthContext';
import { isValidPhone, PHONE_ERROR } from '../lib/phone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const INPUT_CLS = 'bg-[var(--bg-input)] border-[var(--border-input)] text-[var(--text-primary)] placeholder:text-[var(--text-faint)]';

// Haversine distance in km between two lat/lng points.
function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Delivery-zone checker: geocodes the typed address and warns (does NOT block)
// when it falls outside the business's delivery radius. Shown only when the
// business has map coordinates and a delivery radius configured.
function DeliveryZoneCheck({ address, lat, lng, radiusKm, pointLat, pointLng }) {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const hasPoint = Number.isFinite(pointLat) && Number.isFinite(pointLng);
  // Auto-evaluate the zone the moment a precise point is chosen (map pin or an
  // autocomplete pick); clear when the address is free-typed with no point.
  useEffect(() => {
    if (lat == null || lng == null || !radiusKm) { setResult(null); return; }
    if (hasPoint) {
      const d = distanceKm(Number(lat), Number(lng), Number(pointLat), Number(pointLng));
      setResult({ inside: d <= radiusKm, distance: d, auto: true });
    } else {
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, pointLat, pointLng, lat, lng, radiusKm]);
  if (lat == null || lng == null || !radiusKm) return null;
  const canCheck = (address || '').trim().length >= 5;
  const check = async () => {
    setChecking(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/geocode`, { params: { q: address.trim() } });
      const d = distanceKm(Number(lat), Number(lng), Number(data.lat), Number(data.lng));
      setResult({ inside: d <= radiusKm, distance: d });
    } catch { setResult({ error: true }); }
    finally { setChecking(false); }
  };
  return (
    <div className="mt-2" data-testid="delivery-zone-check">
      {!hasPoint && (
        <button type="button" onClick={check} disabled={!canCheck || checking}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 h-8 rounded-full border border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)] disabled:opacity-40 transition-colors"
          data-testid="check-delivery-zone-btn">
          {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Store className="w-3.5 h-3.5" />} Pārbaudīt adresi piegādes zonā
        </button>
      )}
      {result && (
        result.error ? (
          <p className="text-xs text-[var(--text-faint)] mt-1.5" data-testid="delivery-zone-error">Neizdevās atrast adresi. Pārbaudi, vai tā uzrakstīta pareizi.</p>
        ) : result.inside ? (
          <p className="text-xs text-emerald-500 font-semibold mt-1.5 flex items-center gap-1" data-testid="delivery-zone-inside">
            <Check className="w-3.5 h-3.5" /> Adrese ir piegādes zonā (~{result.distance.toFixed(1)} km).
          </p>
        ) : (
          <p className="text-xs text-amber-500 font-semibold mt-1.5" data-testid="delivery-zone-outside">
            ⚠ Adrese ir ārpus piegādes zonas (~{result.distance.toFixed(1)} km, zona ~{radiusKm} km). Vari turpināt, bet piegādi, iespējams, vajadzēs saskaņot ar uzņēmumu.
          </p>
        )
      )}
    </div>
  );
}

export default function BusinessMenuPage({ data, userId, pageId, embedded = false, showMenu, showTables, surfaceId, accentId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const mc = data?.menu || {};
  const effPageId = pageId || data?.id || undefined;
  const availPlugins = pluginsFor('menu');
  const on = (id) => availPlugins.includes(id) && pluginEnabled('menu', mc.plugins, id);
  // When embedded inside the "special" page, the parent decides which modules
  // show (via showMenu / showTables); standalone restaurant pages use on().
  const menuVisible = embedded ? !!showMenu : true;
  const tablesOn = (embedded ? !!showTables : on('tables')) && mc.reservations_enabled;
  const secOrder = orderedSections('menu', mc.module_order);
  const ord = (id) => { const i = secOrder.indexOf(id); return i < 0 ? 99 : i; };
  const [bookingOpen, setBookingOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [slot, setSlot] = useState('');
  const [party, setParty] = useState(2);
  const [form, setForm] = useState({ name: '', phone: '', email: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [taken, setTaken] = useState([]);
  const [confirmCode, setConfirmCode] = useState(null);
  const [payChecking, setPayChecking] = useState(false);
  const [sp, setSp] = useSearchParams();

  const pv = pageVars(embedded ? (surfaceId ?? mc.surface) : mc.surface, embedded ? (accentId ?? mc.accent) : mc.accent, 'menu');
  const hasCover = !!mc.cover_url;
  const heroTitle = hasCover ? '#FFFFFF' : 'var(--text-primary)';
  const heroSub = hasCover ? 'rgba(255,255,255,0.78)' : 'var(--text-muted)';
  const optStyle = { background: 'var(--bg-surface-secondary)', color: 'var(--text-primary)' };

  const deposit = Number(mc.reservation_deposit) || 0;
  const payRequired = deposit > 0;
  const allowOnline = mc.allow_online !== false;
  const allowOnsite = !!mc.allow_onsite;

  const slots = mc.time_slots?.length ? mc.time_slots : ['12:00', '14:00', '16:00', '18:00', '20:00'];

  // ---- Daily-special ordering --------------------------------------------
  const dsPrice = Number(mc.daily_special_price) || 0;
  const dsOrderable = !!mc.daily_special_orderable && dsPrice > 0;
  const dsAllowPickup = mc.daily_special_allow_pickup !== false;
  const dsAllowDelivery = !!mc.daily_special_allow_delivery;
  const delStatus = deliveryStatus(mc.delivery_hours);
  const deliveryClosedNow = delStatus.restricted && !delStatus.openNow;
  const deliveryRows = formatDeliveryHours(mc.delivery_hours);
  const deliveryOpenLabel = deliveryClosedNow ? nextOpenLabel(mc.delivery_hours) : '';
  const deliveryOpenAt = deliveryClosedNow ? (deliveryCountdown(mc.delivery_hours)?.at || '') : '';
  const pickupStatus = deliveryStatus(mc.pickup_hours);
  const pickupClosedNow = pickupStatus.restricted && !pickupStatus.openNow;
  const pickupOpenLabel = pickupClosedNow ? nextOpenLabel(mc.pickup_hours) : '';
  const pickupOpenAt = pickupClosedNow ? (deliveryCountdown(mc.pickup_hours)?.at || '') : '';
  const pickupRows = formatDeliveryHours(mc.pickup_hours);
  const dsMinDelivery = Number(mc.daily_special_min_delivery) || 0;
  const dsDeliveryFee = Number(mc.daily_special_delivery_fee) || 0;
  const dsFreeFrom = Number(mc.daily_special_free_delivery_from) || 0;
  const pkgFee = Number(mc.packaging_fee) || 0;
  const pkgMode = mc.packaging_fee_mode || 'per_order';
  const pkgPickup = mc.packaging_fee_pickup !== false;
  const pkgDelivery = mc.packaging_fee_delivery !== false;
  const dsRadius = Number(mc.daily_special_delivery_radius_km) || 0;
  let eOnline = mc.allow_online !== false;
  let eOnsite = !!mc.allow_onsite;
  if (!eOnline && !eOnsite) eOnline = true;

  // Returned from Stripe (deposit/order paid): poll payment, then confirm.
  useEffect(() => {
    if (embedded) return; // parent (special page) owns the post-payment poll
    const sid = sp.get('session_id');
    if (sp.get('cancelled')) {
      toast.error('Maksājums atcelts');
      sp.delete('cancelled'); setSp(sp, { replace: true });
      return;
    }
    if (!sid || !sp.get('paid')) return;
    setPayChecking(true);
    let attempt = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/business-page/booking/status/${sid}`);
        if (data.payment_status === 'paid') {
          setPayChecking(false);
          toast.success('Maksājums saņemts! Pieprasījums gaida apstiprinājumu.');
          setConfirmCode(true); setBookingOpen(true);
          sp.delete('paid'); sp.delete('session_id'); setSp(sp, { replace: true });
          return;
        }
      } catch { /* keep polling */ }
      attempt += 1;
      if (attempt >= 8) {
        setPayChecking(false);
        toast.message('Maksājums tiek apstrādāts', { description: 'Statuss atjaunosies drīzumā.' });
        sp.delete('paid'); sp.delete('session_id'); setSp(sp, { replace: true });
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
  }, [sp, setSp]);

  const loadMenuAvailability = useCallback(() => {
    if (!bookingOpen || !mc.reservations_enabled) return;
    const d = date.toISOString().slice(0, 10);
    axios.get(`${API}/business-page/${userId}/availability`, { params: { date: d, page_id: effPageId } })
      .then(({ data }) => setTaken(data.taken || [])).catch(() => setTaken([]));
  }, [bookingOpen, date, userId, mc.reservations_enabled, effPageId]);

  useEffect(() => { loadMenuAvailability(); }, [loadMenuAvailability]);

  const [payHold, setPayHold] = useState(null);
  const submitBooking = async (payMethod) => {
    if (!slot) { toast.error('Izvēlies laiku'); return; }
    if (!form.name.trim()) { toast.error('Ievadi vārdu'); return; }
    if (!form.phone.trim()) { toast.error('Norādi telefona numuru'); return; }
    if (!isValidPhone(form.phone)) { toast.error(PHONE_ERROR); return; }
    setSubmitting(true);
    try {
      const d = date.toISOString().slice(0, 10);
      const { data: resp } = await axios.post(`${API}/business-page/${userId}/booking`, {
        page_id: effPageId,
        kind: 'table',
        pay_method: payRequired ? (payMethod || 'online') : undefined,
        service_name: 'Galdiņa rezervācija', date: d, time_slot: slot,
        party_size: Number(party) || 1, name: form.name, phone: form.phone, email: form.email, note: form.note,
        origin_url: window.location.origin,
      });
      if (resp.requires_payment && resp.checkout_url) {
        setBookingOpen(false);
        setPayHold({
          url: resp.checkout_url,
          until: resp.hold_until ? new Date(resp.hold_until).getTime() : Date.now() + (resp.hold_minutes || 15) * 60000,
        });
        return;
      }
      toast.success(resp.pay_choice === 'external'
        ? 'Galdiņš rezervēts! Depozītu apmaksā klātienē vai attālināti.'
        : 'Galdiņa pieprasījums nosūtīts!');
      setConfirmCode(true);
      setSlot(''); setForm({ name: '', phone: '', email: '', note: '' });
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Neizdevās nosūtīt');
      if (e?.response?.status === 409) { setSlot(''); loadMenuAvailability(); }
    } finally { setSubmitting(false); }
  };

  // ---- Menu-item cart ordering -------------------------------------------
  const menuOrderingEnabled = !!mc.menu_ordering_enabled;
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartDone, setCartDone] = useState(false);
  const [cartFulfillment, setCartFulfillment] = useState('pickup');
  const [cartForm, setCartForm] = useState({ name: '', phone: '', email: '', address: '', note: '' });
  const [cartLat, setCartLat] = useState(null);
  const [cartLng, setCartLng] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [cartAddrId, setCartAddrId] = useState(null);      // selected saved-address id, or null = new/manual
  const [saveNewAddr, setSaveNewAddr] = useState(false);
  const [newAddrLabel, setNewAddrLabel] = useState('');
  const [cartSubmitting, setCartSubmitting] = useState(false);
  const [cartTime, setCartTime] = useState('');
  const [cartCode, setCartCode] = useState(null);
  const [cfgItem, setCfgItem] = useState(null);

  const lineKey = (ci, ii, options, addons) => `${ci}-${ii}-${(options || []).map((o) => `${o.gi}:${o.cj}`).join(',')}-${(addons || []).map((a) => a.ai).join(',')}`;
  const lineUnit = (basePrice, options, addons) => Math.round((Number(basePrice || 0) + (options || []).reduce((s, o) => s + Number(o.priceDelta || 0), 0) + (addons || []).reduce((s, a) => s + Number(a.price || 0), 0)) * 100) / 100;
  const hasOptions = (it) => (it.option_groups || []).some((g) => (g.choices || []).some((c) => (c.label || '').trim()));
  const hasAddons = (it) => (it.addons || []).some((a) => (a.label || '').trim());
  const hasConfig = (it) => hasOptions(it) || hasAddons(it);
  const addToCart = (ci, ii, it, options, addons, q = 1, note = '') => {
    const key = lineKey(ci, ii, options, addons);
    setCart((prev) => {
      const idx = prev.findIndex((l) => lineKey(l.ci, l.ii, l.options, l.addons) === key);
      if (idx >= 0) { const n = [...prev]; n[idx] = { ...n[idx], qty: n[idx].qty + q, note: note || n[idx].note }; return n; }
      return [...prev, { ci, ii, source: it._ds ? 'daily_special' : 'menu', name: it.name, basePrice: Number(it.price || 0), options: options || [], addons: addons || [], qty: q, note, packable: it.packaging !== false }];
    });
  };
  const changeNoOptQty = (ci, ii, it, delta) => {
    const key = lineKey(ci, ii, [], []);
    setCart((prev) => {
      const idx = prev.findIndex((l) => lineKey(l.ci, l.ii, l.options, l.addons) === key);
      if (idx < 0) return delta > 0 ? [...prev, { ci, ii, name: it.name, basePrice: Number(it.price || 0), options: [], addons: [], qty: 1, note: '', packable: it.packaging !== false }] : prev;
      const nq = prev[idx].qty + delta;
      if (nq <= 0) return prev.filter((_, i) => i !== idx);
      const n = [...prev]; n[idx] = { ...n[idx], qty: nq }; return n;
    });
  };
  const setLineQty = (idx, q) => setCart((prev) => { if (q <= 0) return prev.filter((_, i) => i !== idx); const n = [...prev]; n[idx] = { ...n[idx], qty: q }; return n; });
  const setLineNote = (idx, note) => setCart((prev) => { const n = [...prev]; n[idx] = { ...n[idx], note }; return n; });
  const cartCount = cart.reduce((s, l) => s + l.qty, 0);
  const cartItemQty = (ci, ii) => cart.filter((l) => l.ci === ci && l.ii === ii).reduce((s, l) => s + l.qty, 0);
  const cartSubtotal = Math.round(cart.reduce((s, l) => s + lineUnit(l.basePrice, l.options, l.addons) * l.qty, 0) * 100) / 100;
  const cartFreeDelivery = dsFreeFrom > 0 && cartSubtotal >= dsFreeFrom;
  const cartDeliveryFee = cartFulfillment === 'delivery' ? (cartFreeDelivery ? 0 : dsDeliveryFee) : 0;
  const cartPackagingApplies = pkgFee > 0 && (cartFulfillment === 'delivery' ? pkgDelivery : pkgPickup);
  const cartPackaging = !cartPackagingApplies ? 0 : (pkgMode === 'per_item'
    ? Math.round(pkgFee * cart.reduce((s, l) => s + (l.packable !== false ? l.qty : 0), 0) * 100) / 100
    : pkgFee);
  const cartTotal = Math.round((cartSubtotal + cartDeliveryFee + cartPackaging) * 100) / 100;
  const cartBelowMin = cartFulfillment === 'delivery' && dsMinDelivery > 0 && cartSubtotal < dsMinDelivery;
  // "Closed now → order for after opening" applies to whichever channel is selected.
  const timeClosedNow = cartFulfillment === 'delivery' ? deliveryClosedNow : pickupClosedNow;
  const timeOpenLabel = cartFulfillment === 'delivery' ? deliveryOpenLabel : pickupOpenLabel;
  const timeOpenAt = cartFulfillment === 'delivery' ? deliveryOpenAt : pickupOpenAt;
  const timeRows = cartFulfillment === 'delivery' ? deliveryRows : pickupRows;
  const openCart = () => {
    setCartDone(false); setCartCode(null); setCartTime(''); setCartFulfillment(dsAllowPickup ? 'pickup' : 'delivery');
    setSaveNewAddr(false); setNewAddrLabel(''); setCartLat(null); setCartLng(null);
    const def = savedAddresses.find((a) => a.is_default) || savedAddresses[0] || null;
    setCartAddrId(def?.id || null);
    setCartForm({
      name: def?.name || user?.name || '',
      phone: def?.phone || user?.phone || '',
      email: user?.email || '',
      address: def?.address || user?.address || '',
      note: '',
    });
    setCartOpen(true);
  };
  const selectSavedAddress = (a) => {
    setCartAddrId(a.id); setSaveNewAddr(false); setCartLat(null); setCartLng(null);
    setCartForm((f) => ({ ...f, address: a.address || '', phone: a.phone || f.phone, name: a.name || f.name }));
  };
  const useNewAddress = () => { setCartAddrId(null); setCartLat(null); setCartLng(null); setCartForm((f) => ({ ...f, address: '' })); };
  // Prefill the table-reservation form from the logged-in user's profile when the dialog opens.
  useEffect(() => {
    if (bookingOpen) {
      setForm((f) => ({ name: f.name || user?.name || '', phone: f.phone || user?.phone || '', email: f.email || user?.email || '', note: f.note || '' }));
    }
  }, [bookingOpen, user]);
  // Load the logged-in user's saved delivery addresses (for cart selection).
  useEffect(() => {
    if (!user) { setSavedAddresses([]); return; }
    axios.get(`${API}/profile/addresses`).then(({ data }) => setSavedAddresses(data?.addresses || [])).catch(() => {});
  }, [user]);
  // Resume a cart saved before the "quick sign-in" redirect, then reopen the cart.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('kolv_cart_resume');
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && saved.pageId === effPageId && Array.isArray(saved.cart) && saved.cart.length) {
        setCart(saved.cart);
        setTimeout(() => openCart(), 300);
      }
      sessionStorage.removeItem('kolv_cart_resume');
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const quickSignIn = () => {
    try { sessionStorage.setItem('kolv_cart_resume', JSON.stringify({ pageId: effPageId, cart })); } catch { /* ignore */ }
    navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  };
  // "Pasūtīt vēlreiz": rebuild the cart from a past order's snapshot (cart_raw)
  // once the live menu is loaded, then open the cart. Items no longer on the
  // menu are skipped.
  const reorderDoneRef = useRef(false);
  useEffect(() => {
    if (reorderDoneRef.current) return;
    let raw;
    try { raw = sessionStorage.getItem('kolv_cart_reorder'); } catch { return; }
    if (!raw) return;
    let saved;
    try { saved = JSON.parse(raw); } catch { sessionStorage.removeItem('kolv_cart_reorder'); return; }
    if (!saved || saved.pageId !== effPageId || !Array.isArray(saved.cartRaw)) return;
    if (!mc || !(mc.categories?.length || mc.daily_special)) return;  // wait for menu data
    const rebuilt = [];
    for (const line of saved.cartRaw) {
      const src = line.source || 'menu';
      const ci = typeof line.category_index === 'number' ? line.category_index : 0;
      const ii = typeof line.item_index === 'number' ? line.item_index : 0;
      let it;
      if (src === 'daily_special') {
        it = { name: mc.daily_special || 'Dienas piedāvājums', price: dsPrice, option_groups: mc.daily_special_option_groups || [], addons: mc.daily_special_addons || [], packaging: mc.daily_special_packaging !== false, _ds: true };
      } else {
        it = mc.categories?.[ci]?.items?.[ii];
      }
      if (!it) continue;
      const options = (line.options || []).map((o) => {
        const g = (it.option_groups || [])[o.group_index];
        const c = g && (g.choices || [])[o.choice_index];
        if (!c) return null;
        return { gi: o.group_index, cj: o.choice_index, label: c.label || '', priceDelta: Number(c.price_delta || 0) };
      }).filter(Boolean);
      const addons = (line.addons || []).map((ai) => {
        const a = (it.addons || [])[ai];
        if (!a) return null;
        return { ai, label: a.label || '', price: Number(a.price || 0) };
      }).filter(Boolean);
      rebuilt.push({ ci: src === 'daily_special' ? 'ds' : ci, ii, source: src, name: it.name, basePrice: Number(it.price || 0), options, addons, qty: line.quantity || 1, note: line.note || '', packable: it.packaging !== false });
    }
    reorderDoneRef.current = true;
    try { sessionStorage.removeItem('kolv_cart_reorder'); } catch { /* ignore */ }
    if (rebuilt.length) {
      setCart(rebuilt);
      setTimeout(() => openCart(), 300);
      toast.success('Pasūtījums ielikts grozā');
    } else {
      toast.error('Šīs preces vairs nav pieejamas');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mc, effPageId]);
  const openItemConfig = (ci, ii, it) => {
    const selections = {};
    (it.option_groups || []).forEach((g, gi) => {
      const firstLabeled = (g.choices || []).findIndex((c) => (c.label || '').trim());
      if (firstLabeled >= 0) selections[gi] = firstLabeled;
    });
    setCfgItem({ ci, ii, it, selections, addons: [], qty: 1, note: '' });
  };
  const confirmItemConfig = () => {
    const { ci, ii, it, selections, addons: addonIdx, qty: q, note } = cfgItem;
    const options = Object.entries(selections).map(([gi, cj]) => {
      const g = it.option_groups[Number(gi)]; const c = (g.choices || [])[cj] || {};
      return { gi: Number(gi), cj: Number(cj), label: c.label || '', priceDelta: Number(c.price_delta || 0) };
    });
    const addons = (addonIdx || []).map((ai) => { const a = (it.addons || [])[ai] || {}; return { ai, label: a.label || '', price: Number(a.price || 0) }; });
    addToCart(ci, ii, it, options, addons, q, note);
    setCfgItem(null);
    toast.success('Pievienots grozā');
  };
  const cfgUnit = cfgItem ? lineUnit(
    cfgItem.it.price,
    Object.entries(cfgItem.selections).map(([gi, cj]) => { const c = (cfgItem.it.option_groups[Number(gi)].choices || [])[cj] || {}; return { priceDelta: Number(c.price_delta || 0) }; }),
    (cfgItem.addons || []).map((ai) => ({ price: Number(((cfgItem.it.addons || [])[ai] || {}).price || 0) }))
  ) : 0;

  // Daily special → add it to the SAME cart as menu items. If it has options /
  // addons configured, open the shared item-config dialog; otherwise add directly.
  const addDailySpecial = () => {
    const it = {
      name: mc.daily_special || 'Dienas piedāvājums',
      price: dsPrice,
      option_groups: mc.daily_special_option_groups || [],
      addons: mc.daily_special_addons || [],
      packaging: mc.daily_special_packaging !== false,
      _ds: true,
    };
    if (hasConfig(it)) { openItemConfig('ds', 0, it); return; }
    addToCart('ds', 0, it, [], [], 1, '');
    toast.success('Pievienots grozā');
  };

  const submitCartOrder = async (payMethod) => {
    if (cart.length === 0) { toast.error('Grozs ir tukšs'); return; }
    if (!cartForm.name.trim()) { toast.error('Ievadi vārdu'); return; }
    if (!cartForm.phone.trim()) { toast.error('Norādi telefona numuru'); return; }
    if (!isValidPhone(cartForm.phone)) { toast.error(PHONE_ERROR); return; }
    if (cartFulfillment === 'delivery' && !cartForm.address.trim()) { toast.error('Norādi piegādes adresi'); return; }
    if (cartBelowMin) { toast.error(`Minimālā pasūtījuma summa piegādei €${dsMinDelivery.toFixed(2)}`); return; }
    setCartSubmitting(true);
    try {
      const cartPayload = cart.map((l) => ({
        source: l.source || 'menu',
        category_index: typeof l.ci === 'number' ? l.ci : 0,
        item_index: typeof l.ii === 'number' ? l.ii : 0,
        quantity: l.qty,
        options: (l.options || []).map((o) => ({ group_index: o.gi, choice_index: o.cj })),
        addons: (l.addons || []).map((a) => a.ai),
        note: l.note || undefined,
      }));
      const { data: resp } = await axios.post(`${API}/business-page/${userId}/booking`, {
        page_id: effPageId, kind: 'order', pay_method: payMethod || 'online',
        cart: cartPayload, fulfillment: cartFulfillment, address: cartForm.address,
        lat: (cartFulfillment === 'delivery' ? cartLat : null) ?? undefined,
        lng: (cartFulfillment === 'delivery' ? cartLng : null) ?? undefined,
        requested_time: cartTime || undefined,
        name: cartForm.name, phone: cartForm.phone, email: cartForm.email,
        note: cartForm.note, origin_url: window.location.origin,
      });
      if (resp.requires_payment && resp.checkout_url) {
        setCartOpen(false);
        setPayHold({ url: resp.checkout_url, until: resp.hold_until ? new Date(resp.hold_until).getTime() : Date.now() + (resp.hold_minutes || 15) * 60000 });
        return;
      }
      setCartCode(resp.code || null);
      toast.success(resp.pay_choice === 'external' ? 'Pasūtījums nosūtīts! Apmaksā saņemot.' : 'Pasūtījums nosūtīts!');
      // Offer to remember a newly typed delivery address on the user's profile.
      if (user && cartFulfillment === 'delivery' && saveNewAddr && cartForm.address.trim()) {
        try {
          const { data: ad } = await axios.post(`${API}/profile/addresses`, {
            label: newAddrLabel.trim() || 'Mana adrese',
            name: cartForm.name, address: cartForm.address.trim(), phone: cartForm.phone,
          });
          setSavedAddresses(ad?.addresses || []);
          toast.success('Adrese saglabāta profilā');
        } catch { /* non-blocking */ }
      }
      setCart([]); setCartDone(true);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Neizdevās nosūtīt');
    } finally { setCartSubmitting(false); }
  };

  return (
    <div className={embedded ? 'w-full' : 'min-h-screen pb-28'} style={embedded ? { ...pv } : { ...pv, background: 'var(--bg-page)', color: 'var(--text-primary)', fontFamily: 'Manrope, sans-serif' }} data-testid={embedded ? 'business-menu-embedded' : 'business-menu-page'}>
      {payHold && <PaymentHoldOverlay url={payHold.url} until={payHold.until} onClose={() => setPayHold(null)} />}
      {/* Hero (standalone restaurant page only) */}
      {!embedded && (
      <div className="relative h-[50vh] min-h-[320px] overflow-hidden">
        {hasCover
          ? <img src={resolveAssetUrl(mc.cover_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 70% 0%, var(--accent-ring), var(--bg-page) 60%)' }} />}
        <div className="absolute inset-0" style={{ background: hasCover
          ? 'linear-gradient(to top, var(--bg-page) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.15) 70%, transparent 100%)'
          : 'linear-gradient(to top, var(--bg-page) 2%, transparent 58%)' }} />
        <div className="relative h-full max-w-5xl mx-auto px-5 flex flex-col justify-end pb-10">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--accent)] mb-3 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {data.owner?.region || 'Latvija'}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-6xl font-light tracking-tighter" style={{ color: heroTitle, fontFamily: 'Outfit, sans-serif' }}>
            {data.owner?.name}
          </motion.h1>
          {mc.tagline && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12 }}
            className="text-base sm:text-lg mt-3 max-w-xl" style={{ color: heroSub }}>{mc.tagline}</motion.p>}
          {(tablesOn || (cartCount > 0)) && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="mt-6 flex flex-col sm:flex-row gap-3">
              {cartCount > 0 && (
                <button onClick={openCart} data-testid="hero-order-btn"
                  className="self-start inline-flex items-center gap-2 h-12 px-8 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] transition-transform hover:scale-105"
                  style={{ boxShadow: '0 10px 40px var(--accent-ring)' }}>
                  <ShoppingBag className="w-5 h-5" /> Veikt pasūtījumu · €{cartSubtotal.toFixed(2)}
                </button>
              )}
              {tablesOn && (
                <button onClick={() => { setConfirmCode(null); setBookingOpen(true); }} data-testid="hero-table-btn"
                  className={`self-start inline-flex items-center gap-2 h-12 px-8 rounded-full font-bold transition-transform hover:scale-105 ${cartCount > 0 ? 'bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)] hover:opacity-80' : 'bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]'}`}
                  style={cartCount > 0 ? {} : { boxShadow: '0 10px 40px var(--accent-ring)' }}>
                  <CalendarClock className="w-5 h-5" /> Rezervēt galdiņu
                </button>
              )}
            </motion.div>
          )}
        </div>
      </div>
      )}

      <div className={embedded ? 'w-full flex flex-col gap-10' : 'max-w-5xl mx-auto px-5 flex flex-col gap-12 sm:gap-14 mt-12'}>
        {embedded && (menuVisible || tablesOn) && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="menu-embedded-header">
            <h2 className="text-2xl font-medium text-[var(--text-primary)] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <UtensilsCrossed className="w-6 h-6 text-[var(--accent)]" /> {menuVisible ? 'Ēdienkarte & pasūtīšana' : 'Galdiņu rezervācija'}
            </h2>
            <div className="flex flex-wrap gap-3">
              {cartCount > 0 && (
                <button onClick={openCart} data-testid="embedded-order-btn"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] transition-transform hover:scale-105">
                  <ShoppingBag className="w-5 h-5" /> Grozs · €{cartSubtotal.toFixed(2)}
                </button>
              )}
              {tablesOn && (
                <button onClick={() => { setConfirmCode(null); setBookingOpen(true); }} data-testid="embedded-table-btn"
                  className={`inline-flex items-center gap-2 h-11 px-6 rounded-full font-bold transition-transform hover:scale-105 ${cartCount > 0 ? 'bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)] hover:opacity-80' : 'bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]'}`}>
                  <CalendarClock className="w-5 h-5" /> Rezervēt galdiņu
                </button>
              )}
            </div>
          </div>
        )}
        {menuVisible && dsAllowDelivery && (
          <div style={{ order: -3 }} data-testid="delivery-banner-wrap">
            <DeliveryStatusBanner dh={mc.delivery_hours} />
          </div>
        )}
        {!embedded && (
        <section data-testid="contact-business-section" className="flex" style={{ order: -2 }}>
          <ContactBusinessButton ownerId={userId} ownerName={data.owner?.name} />
        </section>
        )}
        {/* Daily special */}
        {menuVisible && mc.daily_special && (
          <section data-testid="daily-special" style={{ order: ord('daily_special') }}>
            <div className="rounded-3xl border border-[var(--accent-border)] p-6 flex flex-col sm:flex-row sm:items-center gap-4 shadow-sm"
              style={{ background: 'linear-gradient(135deg, var(--accent-soft), transparent)' }}>
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <Flame className="w-7 h-7 text-[var(--accent)] shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--accent)] mb-1">Dienas piedāvājums</p>
                  <p className="text-[var(--text-primary)] text-lg">{mc.daily_special}</p>
                  {dsOrderable && <p className="text-[var(--accent)] font-bold text-xl mt-1" data-testid="daily-special-price">€{dsPrice.toFixed(2)}</p>}
                </div>
              </div>
              {dsOrderable && (
                <button onClick={addDailySpecial} data-testid="order-daily-special-btn"
                  className="shrink-0 inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] transition-transform hover:scale-105 w-full sm:w-auto">
                  <ShoppingBag className="w-5 h-5" /> {cartItemQty('ds', 0) > 0 ? `Grozā (${cartItemQty('ds', 0)})` : 'Pasūtīt'}
                </button>
              )}
            </div>
          </section>
        )}

        {/* Menu categories */}
        {menuVisible && mc.categories?.length > 0 && (
          <section data-testid="menu-section" style={{ order: ord('menu') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-6 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <UtensilsCrossed className="w-6 h-6 text-[var(--accent)]" /> Ēdienkarte
            </h2>
            <div className="space-y-10">
              {mc.categories.map((c, ci) => (
                <div key={c.id || ci} data-testid={`menu-category-${ci}`}>
                  <h3 className="text-[var(--accent)] text-sm font-bold uppercase tracking-[0.18em] mb-4 border-b border-[var(--border-divider)] pb-2">{c.name}</h3>
                  <div className="space-y-4">
                    {c.items?.map((it, ii) => (
                      <div key={ii} className="flex items-baseline gap-3" data-testid={`menu-item-${ci}-${ii}`}>
                        {(() => { const imgs = itemImages(it); return imgs.length > 0 && (
                          <div className="relative shrink-0 self-center" onClick={() => setLightbox({ images: imgs, index: 0 })}>
                            <img src={resolveAssetUrl(imgs[0])} alt={it.name} className="w-14 h-14 rounded-lg object-cover cursor-zoom-in" data-testid={`menu-item-img-${ci}-${ii}`} />
                            {imgs.length > 1 && <span className="absolute bottom-0.5 right-0.5 text-[9px] font-bold px-1 rounded bg-black/70 text-white leading-tight">+{imgs.length - 1}</span>}
                          </div>
                        ); })()}
                        <div className="min-w-0">
                          <p className="text-[var(--text-primary)] font-medium flex items-center gap-2 flex-wrap">
                            {it.name}
                            {it.tags?.map((tg, ti) => (
                              <span key={ti} className="text-[10px] font-semibold uppercase tracking-wide text-[var(--accent)] bg-[var(--accent-soft)] px-1.5 py-0.5 rounded-full">{tg}</span>
                            ))}
                          </p>
                          {it.description && <p className="text-[var(--text-faint)] text-sm mt-0.5">{it.description}</p>}
                        </div>
                        <span className="flex-1 border-b border-dashed border-[var(--border-divider)] translate-y-[-4px]" />
                        {it.price != null && <span className="text-[var(--accent)] font-light text-lg shrink-0 self-center">€{it.price}</span>}
                        {menuOrderingEnabled && it.price != null && (
                          hasConfig(it) ? (
                            <button onClick={() => openItemConfig(ci, ii, it)} data-testid={`additem-${ci}-${ii}`}
                              className="self-center shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] text-sm font-semibold hover:bg-[var(--accent-hover)]">
                              <Plus className="w-4 h-4" /> Pievienot
                            </button>
                          ) : cartItemQty(ci, ii) > 0 ? (
                            <div className="self-center shrink-0 flex items-center gap-2" data-testid={`item-stepper-${ci}-${ii}`}>
                              <button onClick={() => changeNoOptQty(ci, ii, it, -1)} className="w-8 h-8 rounded-full bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)] flex items-center justify-center" data-testid={`item-minus-${ci}-${ii}`}><Minus className="w-4 h-4" /></button>
                              <span className="w-5 text-center font-semibold text-[var(--text-primary)]" data-testid={`item-qty-${ci}-${ii}`}>{cartItemQty(ci, ii)}</span>
                              <button onClick={() => changeNoOptQty(ci, ii, it, 1)} className="w-8 h-8 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] flex items-center justify-center" data-testid={`item-plus-${ci}-${ii}`}><Plus className="w-4 h-4" /></button>
                            </div>
                          ) : (
                            <button onClick={() => changeNoOptQty(ci, ii, it, 1)} data-testid={`additem-${ci}-${ii}`}
                              className="self-center shrink-0 w-9 h-9 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] flex items-center justify-center hover:bg-[var(--accent-hover)]">
                              <Plus className="w-4 h-4" />
                            </button>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Promotions + Working hours & map (optional modules; FAQ/reviews handled by BusinessExtras) — standalone restaurant page only; the special page renders these from its own reservations config. */}
        {!embedded && (<>
        {on('promotions') && <PromotionsSection promotions={mc.promotions} order={ord('promotions')} />}
        <HoursMapSection showHours={on('hours')} hours={mc.working_hours} showMap={on('map')} address={mc.address} lat={mc.lat} lng={mc.lng} order={ord('hours_map')} deliveryHours={mc.delivery_hours} />

        {/* Gift cards (optional module, OFF by default) */}
        {on('gift_cards') && (
          <BusinessGiftCards userId={userId} pageId={effPageId} cards={mc.gift_cards || []} order={ord('gift_cards')} pv={pv} />
        )}

        {/* Gallery */}
        {on('gallery') && mc.gallery?.length > 0 && (
          <section data-testid="menu-gallery" style={{ order: ord('gallery') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>Galerija</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {mc.gallery.map((g, i) => (
                <img key={i} src={resolveAssetUrl(g)} alt="" onClick={() => setLightbox({ images: mc.gallery, index: i })} className={`w-full rounded-2xl object-cover cursor-zoom-in ${i % 3 === 0 ? 'h-64 row-span-2' : 'h-32'}`} data-testid={`menu-gallery-image-${i}`} />
              ))}
            </div>
          </section>
        )}

        <BusinessExtras template="menu" config={mc} userId={userId} pageId={effPageId} ord={ord} />
        </>)}
      </div>

      {/* Sticky mobile CTA (standalone restaurant page only) */}
      {!embedded && (tablesOn || (cartCount > 0)) && (
        <div className="fixed bottom-0 inset-x-0 z-40 p-3 backdrop-blur-xl border-t border-[var(--border-card)] md:hidden" style={{ ...pv, background: 'var(--bg-header)', paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
          <div className="flex flex-col gap-2">
            {cartCount > 0 && (
              <button onClick={openCart} className="w-full h-12 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold flex items-center justify-center gap-2" data-testid="sticky-order-btn">
                <ShoppingBag className="w-5 h-5" /> Veikt pasūtījumu · €{cartSubtotal.toFixed(2)}
              </button>
            )}
            {tablesOn && (
              <button onClick={() => { setConfirmCode(null); setBookingOpen(true); }} className={`w-full h-12 rounded-full font-bold flex items-center justify-center gap-2 ${cartCount > 0 ? 'bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)]' : 'bg-[var(--accent)] text-[var(--accent-contrast)]'}`} data-testid="sticky-table-btn">
                <CalendarClock className="w-5 h-5" /> Rezervēt galdiņu
              </button>
            )}
          </div>
        </div>
      )}

      {/* Embedded (special page) floating cart button — avoids the full-width bar
          so it never clashes with the special page's own sticky CTA. */}
      {embedded && cartCount > 0 && (
        <button onClick={openCart} data-testid="embedded-cart-fab"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 h-14 px-6 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold shadow-[0_10px_40px_var(--accent-ring)] hover:bg-[var(--accent-hover)] transition-transform hover:scale-105"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <ShoppingBag className="w-5 h-5" /> Grozs ({cartCount}) · €{cartSubtotal.toFixed(2)}
        </button>
      )}

      {/* Payment verification overlay (returning from Stripe) */}
      {payChecking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'var(--bg-overlay)' }} data-testid="table-pay-checking">
          <div className="border border-[var(--border-card)] rounded-2xl p-8 text-center" style={{ ...pv, background: 'var(--bg-surface-secondary)' }}>
            <Loader2 className="w-8 h-8 animate-spin text-[var(--accent)] mx-auto mb-3" />
            <p className="text-[var(--text-primary)] font-semibold">Pārbaudām maksājumu…</p>
            <p className="text-[var(--text-faint)] text-sm mt-1">Lūdzu uzgaidi mirkli.</p>
          </div>
        </div>
      )}

      <ImageLightbox value={lightbox} onClose={() => setLightbox(null)} />

      {/* Booking dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent style={{ ...pv, background: 'var(--bg-surface-secondary)', borderColor: 'var(--border-card)' }} className="text-[var(--text-primary)] max-h-[88vh] overflow-y-auto w-[92vw] sm:w-full max-w-lg" data-testid="table-booking-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Galdiņa rezervācija</DialogTitle>
            <DialogDescription className="text-[var(--text-muted)]">Izvēlies datumu, laiku un personu skaitu.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {confirmCode ? (
              <div className="text-center py-4" data-testid="table-booking-confirmation">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[var(--accent-soft)] flex items-center justify-center">
                  <Check className="w-7 h-7 text-[var(--accent)]" />
                </div>
                <p className="text-[var(--text-primary)] font-semibold mb-1">Pieprasījums nosūtīts!</p>
                <p className="text-[var(--text-faint)] text-sm mb-4">Rezervācija gaida restorāna apstiprinājumu. Kad tā tiks apstiprināta, saņemsi apstiprinājuma kodu{form.email ? ' e-pastā' : ''}, ko uzrādīt ierodoties.</p>
                <button onClick={() => { setConfirmCode(null); setBookingOpen(false); }} className="w-full h-11 rounded-full bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)] font-semibold hover:opacity-80" data-testid="table-booking-done">Aizvērt</button>
              </div>
            ) : (
            <>
            <div className="[&_.rdp]:text-[var(--text-primary)] flex justify-center">
              <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={{ before: new Date() }} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={slot} onChange={(e) => setSlot(e.target.value)} className="h-11 rounded-xl bg-[var(--bg-input)] border border-[var(--border-input)] text-[var(--text-primary)] text-sm px-3" data-testid="table-slot-select">
                <option value="" style={optStyle}>Laiks</option>
                {slots.map((s) => <option key={s} value={s} disabled={taken.includes(s)} style={optStyle}>{s}{taken.includes(s) ? ' — aizņemts' : ''}</option>)}
              </select>
              <div className="flex items-center gap-2 rounded-xl bg-[var(--bg-input)] border border-[var(--border-input)] px-3">
                <Users className="w-4 h-4 text-[var(--text-faint)]" />
                <input type="number" min="1" max="30" value={party} onChange={(e) => setParty(e.target.value)} className="w-full bg-transparent text-sm outline-none text-[var(--text-primary)]" data-testid="table-party-size" placeholder="Personas" />
              </div>
            </div>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tavs vārds" className={INPUT_CLS} data-testid="table-name" />
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefons *" className={INPUT_CLS} data-testid="table-phone" />
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-pasts (lai saņemtu apstiprinājumu)" className={INPUT_CLS} data-testid="table-email" />
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Piezīme (neobligāti)" className={INPUT_CLS} data-testid="table-note" />
            {payRequired && (
              <div className="rounded-xl bg-[var(--accent-soft)] border border-[var(--accent-border)] px-3 py-2 text-[var(--text-muted)] text-xs" data-testid="table-payment-note">
                Galdiņa depozīts €{deposit.toFixed(2)}. {allowOnline && allowOnsite ? 'Izvēlies, kā maksāt: tagad online ar karti caur KoLV, vai klātienē/attālināti.' : allowOnline ? 'Apmaksa online ar karti caur KoLV.' : 'Apmaksa klātienē vai attālināti.'}
              </div>
            )}
            {payRequired ? (
              <div className="space-y-2">
                {allowOnline && (
                  <button onClick={() => submitBooking('online')} disabled={submitting}
                    className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="submit-table-online">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Maksāt tagad €{deposit.toFixed(2)}
                  </button>
                )}
                {allowOnsite && (
                  <button onClick={() => submitBooking('external')} disabled={submitting}
                    className={`w-full h-11 rounded-full disabled:opacity-60 flex items-center justify-center gap-2 font-semibold ${allowOnline ? 'border border-[var(--border-card)] text-[var(--text-primary)] hover:bg-[var(--bg-card)]' : 'bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)]'}`} data-testid="submit-table-external">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Maksāt klātienē / attālināti
                  </button>
                )}
              </div>
            ) : (
              <button onClick={() => submitBooking()} disabled={submitting}
                className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="submit-table-booking">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Nosūtīt pieprasījumu
              </button>
            )}
            </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Menu cart order dialog */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent style={{ ...pv, background: 'var(--bg-surface-secondary)', borderColor: 'var(--border-card)' }} className="text-[var(--text-primary)] max-h-[88vh] overflow-y-auto w-[92vw] sm:w-full max-w-lg" data-testid="cart-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Tavs pasūtījums</DialogTitle>
            <DialogDescription className="text-[var(--text-muted)]">Pārskati grozu, izvēlies saņemšanu un apmaksā.</DialogDescription>
          </DialogHeader>
          {cartDone ? (
            <div className="text-center py-4" data-testid="cart-confirmation">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[var(--accent-soft)] flex items-center justify-center"><Check className="w-7 h-7 text-[var(--accent)]" /></div>
              <p className="text-[var(--text-primary)] font-semibold mb-1">Pasūtījums nosūtīts!</p>
              <p className="text-[var(--text-faint)] text-sm mb-3">Pasūtījums gaida apstiprinājumu. Kad tas tiks apstiprināts, saņemsi ziņu{cartForm.email ? ' e-pastā' : ''}.</p>
              {cartCode && (
                <div className="mb-4">
                  <p className="text-[var(--text-faint)] text-xs">Pasūtījuma kods</p>
                  <p className="font-mono text-lg font-bold text-[var(--accent)]" data-testid="cart-code">{cartCode}</p>
                  <a href={`/pasutijums/${cartCode}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-[var(--accent)] hover:underline" data-testid="cart-track-link">Sekot pasūtījumam →</a>
                </div>
              )}
              <button onClick={() => setCartOpen(false)} className="w-full h-11 rounded-full bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)] font-semibold hover:opacity-80" data-testid="cart-done">Aizvērt</button>
            </div>
          ) : cart.length === 0 ? (
            <div className="text-center py-8 text-[var(--text-faint)]" data-testid="cart-empty">Grozs ir tukšs. Pievieno ēdienus no ēdienkartes.</div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2" data-testid="cart-lines">
                {cart.map((l, idx) => (
                  <div key={idx} className="rounded-xl bg-[var(--bg-card)] border border-[var(--border-card)] p-3" data-testid={`cart-line-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[var(--text-primary)] font-medium text-sm">{l.name}</p>
                        {(l.options?.length > 0 || l.addons?.length > 0) && <p className="text-[var(--text-faint)] text-xs mt-0.5">{[...(l.options || []).map((o) => o.label), ...(l.addons || []).map((a) => `+ ${a.label}`)].join(', ')}</p>}
                        <p className="text-[var(--accent)] text-xs font-semibold mt-0.5">€{lineUnit(l.basePrice, l.options, l.addons).toFixed(2)} / gab.</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => setLineQty(idx, l.qty - 1)} className="w-8 h-8 rounded-full bg-[var(--bg-input)] border border-[var(--border-input)] flex items-center justify-center" data-testid={`cart-minus-${idx}`}><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-6 text-center text-sm font-semibold" data-testid={`cart-qty-${idx}`}>{l.qty}</span>
                        <button onClick={() => setLineQty(idx, l.qty + 1)} className="w-8 h-8 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] flex items-center justify-center" data-testid={`cart-plus-${idx}`}><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button onClick={() => setLineQty(idx, 0)} className="shrink-0 w-8 h-8 rounded-full text-[var(--text-faint)] hover:text-red-500 flex items-center justify-center" data-testid={`cart-remove-${idx}`}><X className="w-4 h-4" /></button>
                    </div>
                    <Input value={l.note || ''} onChange={(e) => setLineNote(idx, e.target.value)} placeholder="Piezīme ēdienam (neobligāti)" className={`${INPUT_CLS} mt-2 h-9 text-sm`} data-testid={`cart-note-${idx}`} />
                  </div>
                ))}
              </div>

              {(dsAllowPickup || dsAllowDelivery) && (
                <div className="grid grid-cols-2 gap-2" data-testid="cart-fulfillment">
                  {dsAllowPickup && (
                    <button type="button" onClick={() => setCartFulfillment('pickup')} className={`min-h-[44px] rounded-xl text-sm font-semibold border flex items-center justify-center gap-2 transition-colors ${cartFulfillment === 'pickup' ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)]'}`} data-testid="cart-pickup-btn"><Store className="w-4 h-4" /> Saņemt uz vietas</button>
                  )}
                  {dsAllowDelivery && (
                    <button type="button" onClick={() => setCartFulfillment('delivery')} className={`min-h-[44px] rounded-xl text-sm font-semibold border flex items-center justify-center gap-2 transition-colors ${cartFulfillment === 'delivery' ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)]'}`} data-testid="cart-delivery-btn"><Truck className="w-4 h-4" /> Piegāde</button>
                  )}
                </div>
              )}

              {cartFulfillment === 'delivery' && (
                <>
                  {user && savedAddresses.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="saved-address-chips">
                      {savedAddresses.map((a) => (
                        <button key={a.id} type="button" onClick={() => selectSavedAddress(a)}
                          className={`px-3 h-9 rounded-full text-sm font-semibold border transition-colors ${cartAddrId === a.id ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)]'}`}
                          data-testid={`cart-addr-chip-${a.id}`}>{a.label || a.address}</button>
                      ))}
                      <button type="button" onClick={useNewAddress}
                        className={`px-3 h-9 rounded-full text-sm font-semibold border transition-colors ${cartAddrId === null ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)]'}`}
                        data-testid="cart-addr-chip-new">+ Cita adrese</button>
                    </div>
                  )}
                  <DeliveryAddressField
                    address={cartForm.address}
                    lat={cartLat}
                    lng={cartLng}
                    fallbackLat={mc.lat}
                    fallbackLng={mc.lng}
                    inputCls={INPUT_CLS}
                    placeholder="Piegādes adrese *"
                    onChange={({ address, lat, lng }) => {
                      setCartForm((f) => ({ ...f, address }));
                      setCartLat(lat ?? null); setCartLng(lng ?? null);
                      setCartAddrId(null);
                    }}
                  />
                  {user && cartAddrId === null && cartForm.address.trim() && !savedAddresses.some((a) => (a.address || '').trim() === cartForm.address.trim()) && (
                    <div className="rounded-xl border border-[var(--border-input)] p-2.5 space-y-2" data-testid="save-address-box">
                      <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] cursor-pointer">
                        <input type="checkbox" checked={saveNewAddr} onChange={(e) => setSaveNewAddr(e.target.checked)} className="accent-[var(--accent)]" data-testid="save-address-check" />
                        Saglabāt šo adresi profilā turpmākajiem pasūtījumiem
                      </label>
                      {saveNewAddr && (
                        <Input value={newAddrLabel} onChange={(e) => setNewAddrLabel(e.target.value)} placeholder="Nosaukums (piem. Mājas, Darbs)" className={INPUT_CLS} data-testid="new-address-label" />
                      )}
                    </div>
                  )}
                  {(dsMinDelivery > 0 || dsFreeFrom > 0 || dsRadius > 0) && (
                    <div className="text-xs space-y-0.5" data-testid="cart-delivery-rules">
                      {dsRadius > 0 && <p className="text-[var(--text-faint)]">Piegādājam ~{dsRadius} km rādiusā. Ārpus zonas — sarunā piegādi čatā.</p>}
                      {dsMinDelivery > 0 && <p className={cartBelowMin ? 'text-red-500' : 'text-[var(--text-faint)]'}>Min. pasūtījums piegādei: €{dsMinDelivery.toFixed(2)}{cartBelowMin ? ` — trūkst €${(dsMinDelivery - cartSubtotal).toFixed(2)}` : ''}</p>}
                      {dsFreeFrom > 0 && <p className={cartFreeDelivery ? 'text-[var(--accent)] font-semibold' : 'text-[var(--text-faint)]'}>{cartFreeDelivery ? 'Bezmaksas piegāde! 🎉' : `Bezmaksas piegāde no €${dsFreeFrom.toFixed(2)}`}</p>}
                    </div>
                  )}
                  <DeliveryZoneCheck address={cartForm.address} lat={mc.lat} lng={mc.lng} radiusKm={dsRadius} pointLat={cartLat} pointLng={cartLng} />
                </>
              )}

              <div data-testid="cart-time">
                <p className="text-[var(--text-primary)] text-sm font-semibold mb-2">{cartFulfillment === 'delivery' ? 'Piegādes laiks' : 'Saņemšanas laiks'}</p>
                {timeClosedNow && (
                  <div className="rounded-xl border border-[var(--accent-border)] bg-[var(--accent-soft)] p-2.5 mb-2" data-testid={cartFulfillment === 'delivery' ? 'delivery-closed-notice' : 'pickup-closed-note'}>
                    <p className="text-[var(--text-muted)] text-xs">{cartFulfillment === 'delivery' ? 'Piegāde šobrīd slēgta — pasūtījums tiks piegādāts pēc atvēršanas' : 'Šobrīd slēgts — saņemšana iespējama pēc atvēršanas'}{timeOpenLabel ? ` (${timeOpenLabel})` : ''}.</p>
                    {timeRows.length > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        {timeRows.map((r, i) => (<div key={i} className="flex justify-between gap-4 text-[var(--text-faint)] text-xs"><span>{r.days}</span><span>{r.text}</span></div>))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {timeClosedNow ? (
                    <button type="button" onClick={() => setCartTime(`Pēc atvēršanas (${timeOpenLabel})`)}
                      className={`px-3 h-9 rounded-full text-sm font-semibold border transition-colors ${cartTime.startsWith('Pēc atvēršanas') ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)]'}`} data-testid="cart-time-afteropen">
                      Pēc atvēršanas{timeOpenLabel ? ` (${timeOpenLabel})` : ''}
                    </button>
                  ) : (
                    [['Pēc iespējas ātrāk', 'asap'], ['Pēc 30 min', '30'], ['Pēc 1 h', '1h']].map(([t, id]) => (
                      <button key={id} type="button" onClick={() => setCartTime(t)} className={`px-3 h-9 rounded-full text-sm font-semibold border transition-colors ${cartTime === t ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)]'}`} data-testid={`cart-time-${id}`}>{t}</button>
                    ))
                  )}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[var(--text-faint)] text-sm">vai plkst.</span>
                    <input type="time" min={(timeClosedNow && timeOpenAt) ? timeOpenAt : undefined} value={cartTime.startsWith('Plkst.') ? cartTime.replace('Plkst. ', '') : ''} onChange={(e) => setCartTime(e.target.value ? `Plkst. ${e.target.value}` : '')} className={`${INPUT_CLS} h-9 rounded-lg px-2 text-sm`} data-testid="cart-time-specific" />
                  </div>
                </div>
              </div>

              {!user && (
                <button type="button" onClick={quickSignIn}
                  className="w-full flex items-center justify-between gap-2 rounded-xl border border-[var(--accent-border)] bg-[var(--accent-soft)] px-3.5 py-2.5 text-left transition-colors hover:bg-[var(--accent-ring)]"
                  data-testid="cart-quick-signin">
                  <span className="text-sm text-[var(--text-primary)]"><span className="font-semibold">Pierakstīties</span>, lai aizpildītu ātrāk</span>
                  <span className="text-[var(--accent)] text-sm font-semibold shrink-0">Pierakstīties →</span>
                </button>
              )}

              <Input value={cartForm.name} onChange={(e) => setCartForm({ ...cartForm, name: e.target.value })} placeholder="Tavs vārds *" className={INPUT_CLS} data-testid="cart-name" />
              <Input value={cartForm.phone} onChange={(e) => setCartForm({ ...cartForm, phone: e.target.value })} placeholder="Telefons *" className={INPUT_CLS} data-testid="cart-phone" />
              <Input type="email" value={cartForm.email} onChange={(e) => setCartForm({ ...cartForm, email: e.target.value })} placeholder="E-pasts (apstiprinājumam)" className={INPUT_CLS} data-testid="cart-email" />
              <Textarea value={cartForm.note} onChange={(e) => setCartForm({ ...cartForm, note: e.target.value })} placeholder="Piezīme pasūtījumam (neobligāti)" className={INPUT_CLS} data-testid="cart-order-note" />

              <div className="rounded-xl bg-[var(--bg-card)] border border-[var(--border-card)] px-4 py-3 text-sm space-y-1" data-testid="cart-total">
                <div className="flex justify-between text-[var(--text-muted)]"><span>Summa ({cartCount} vnt.)</span><span>€{cartSubtotal.toFixed(2)}</span></div>
                {cartFulfillment === 'delivery' && <div className="flex justify-between text-[var(--text-muted)]"><span>Piegāde</span><span>{cartFreeDelivery ? <span className="text-[var(--accent)] font-semibold">Bezmaksas</span> : `€${cartDeliveryFee.toFixed(2)}`}</span></div>}
                {cartPackaging > 0 && <div className="flex justify-between text-[var(--text-muted)]" data-testid="cart-packaging-row"><span>Iepakojums{pkgMode === 'per_item' ? ` (×${cart.reduce((s, l) => s + (l.packable !== false ? l.qty : 0), 0)})` : ''}</span><span>€{cartPackaging.toFixed(2)}</span></div>}
                <div className="flex justify-between text-[var(--text-primary)] font-bold text-base pt-1"><span>Kopā</span><span className="text-[var(--accent)]">€{cartTotal.toFixed(2)}</span></div>
              </div>

              <div className="space-y-2">
                {eOnline && (
                  <button onClick={() => submitCartOrder('online')} disabled={cartSubmitting || cartBelowMin} className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="confirm-cart-order">
                    {cartSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Apstiprināt un maksāt €{cartTotal.toFixed(2)}
                  </button>
                )}
                {eOnsite && (
                  <button onClick={() => submitCartOrder('external')} disabled={cartSubmitting || cartBelowMin} className={`w-full h-11 rounded-full disabled:opacity-60 flex items-center justify-center gap-2 font-semibold ${eOnline ? 'border border-[var(--border-card)] text-[var(--text-primary)] hover:bg-[var(--bg-card)]' : 'bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]'}`} data-testid="confirm-cart-order-onsite">
                    {cartSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Maksāt saņemot
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Item options config dialog */}
      <Dialog open={!!cfgItem} onOpenChange={(o) => { if (!o) setCfgItem(null); }}>
        <DialogContent style={{ ...pv, background: 'var(--bg-surface-secondary)', borderColor: 'var(--border-card)' }} className="text-[var(--text-primary)] max-h-[88vh] overflow-y-auto w-[92vw] sm:w-full max-w-md" data-testid="item-config-dialog">
          {cfgItem && (
            <>
              <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>{cfgItem.it.name}</DialogTitle>
                <DialogDescription className="text-[var(--text-muted)]">{cfgItem.it.description || 'Izvēlies opcijas un daudzumu.'}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {(cfgItem.it.option_groups || []).map((g, gi) => {
                  const labeled = (g.choices || []).map((c, cj) => ({ c, cj })).filter(({ c }) => (c.label || '').trim());
                  if (labeled.length === 0) return null;
                  return (
                  <div key={gi} data-testid={`cfg-group-${gi}`}>
                    <p className="text-[var(--text-primary)] text-sm font-semibold mb-2">{g.name || 'Izvēle'}</p>
                    <div className="grid grid-cols-1 gap-2">
                      {labeled.map(({ c, cj }) => (
                        <button key={cj} type="button" onClick={() => setCfgItem((p) => ({ ...p, selections: { ...p.selections, [gi]: cj } }))}
                          className={`flex items-center justify-between min-h-[42px] px-3 rounded-xl text-sm border transition-colors ${cfgItem.selections[gi] === cj ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)]'}`} data-testid={`cfg-choice-${gi}-${cj}`}>
                          <span>{c.label}</span>
                          {Number(c.price_delta || 0) !== 0 && <span className="text-xs font-semibold">{Number(c.price_delta) > 0 ? '+' : ''}{Number(c.price_delta).toFixed(2)} €</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                  );
                })}
                {hasAddons(cfgItem.it) && (
                  <div data-testid="cfg-addons">
                    <p className="text-[var(--text-primary)] text-sm font-semibold mb-2">Papildinājumi</p>
                    <div className="grid grid-cols-1 gap-2">
                      {(cfgItem.it.addons || []).map((a, ai) => (a.label || '').trim() ? (
                        <button key={ai} type="button" onClick={() => setCfgItem((p) => ({ ...p, addons: p.addons.includes(ai) ? p.addons.filter((x) => x !== ai) : [...p.addons, ai] }))}
                          className={`flex items-center justify-between min-h-[42px] px-3 rounded-xl text-sm border transition-colors ${cfgItem.addons.includes(ai) ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)]'}`} data-testid={`cfg-addon-${ai}`}>
                          <span className="flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> {a.label}</span>
                          {Number(a.price || 0) > 0 && <span className="text-xs font-semibold">+{Number(a.price).toFixed(2)} €</span>}
                        </button>
                      ) : null)}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between rounded-xl bg-[var(--bg-card)] border border-[var(--border-card)] px-4 py-2.5">
                  <span className="text-[var(--text-muted)] text-sm">Daudzums</span>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setCfgItem((p) => ({ ...p, qty: Math.max(1, p.qty - 1) }))} className="w-9 h-9 rounded-full bg-[var(--bg-input)] border border-[var(--border-input)] flex items-center justify-center" data-testid="cfg-qty-minus"><Minus className="w-4 h-4" /></button>
                    <span className="w-6 text-center font-semibold" data-testid="cfg-qty-value">{cfgItem.qty}</span>
                    <button type="button" onClick={() => setCfgItem((p) => ({ ...p, qty: Math.min(50, p.qty + 1) }))} className="w-9 h-9 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] flex items-center justify-center" data-testid="cfg-qty-plus"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>
                <Input value={cfgItem.note} onChange={(e) => setCfgItem((p) => ({ ...p, note: e.target.value }))} placeholder="Piezīme (neobligāti)" className={INPUT_CLS} data-testid="cfg-note" />
                <button onClick={confirmItemConfig} className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] flex items-center justify-center gap-2" data-testid="cfg-add-btn">
                  <ShoppingBag className="w-4 h-4" /> Pievienot grozā · €{(cfgUnit * cfgItem.qty).toFixed(2)}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
